import { useMemo } from "react";

/* ───────── helpers PUROS (fuera del hook) ───────── */
const monthsAbbrev = [
  "Ene",
  "Feb",
  "Mar",
  "Abr",
  "May",
  "Jun",
  "Jul",
  "Ago",
  "Sep",
  "Oct",
  "Nov",
  "Dic",
];

const monthsFull = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

/* ----- formato de fechas ----- */
function formatDate(dateString, returnFormats = false) {
  let date;
  // Detectar strings "YYYY-MM-DD" para crear fecha en zona local
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split('-').map(Number);
    date = new Date(year, month - 1, day);
  } else {
    date = new Date(dateString);
  }

  if (Number.isNaN(date.getTime())) return returnFormats ? [""] : "";

  const dayStr = date.getDate().toString().padStart(2, "0");
  const dayNoPad = date.getDate().toString();
  const monthAbbrev = monthsAbbrev[date.getMonth()];
  const monthFull = monthsFull[date.getMonth()];
  const yearTwo = date.getFullYear().toString().slice(-2);

  const formatted = `${dayStr} ${monthAbbrev} ${yearTwo}`;

  if (!returnFormats) return formatted;
  return [
    formatted.toLowerCase(),
    `${dayNoPad} ${monthFull}`.toLowerCase(),
    `${dayNoPad}, ${monthFull}`.toLowerCase(),
    `${dayNoPad} ${monthFull} ${yearTwo}`.toLowerCase(),
    `${dayStr} ${monthFull}`.toLowerCase(),
    monthFull,
    monthAbbrev.toLowerCase(),
  ];
}

/* ----- formato de moneda ----- */
function formatCurrency(num = 0) {
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

/* ----- normalización de números ----- */
const trimLeadingZeros = (v = "") =>
  typeof v === "string" ? v.replace(/^0+(?=\d)/, "") : v;

/* ------------------------------------------------------------------ */
/* ---------- MÉTRICAS Y DASHBOARD ---------- */

/** Sub-, IVA y total de un arreglo de productos */
function calcTotals(rows = []) {
  const subtotal = rows.reduce(
    (s, { cantidad = 0, precioUnitario = 0 }) => s + cantidad * precioUnitario,
    0
  );
  const iva = subtotal * 0.16;
  return { subtotal, iva, total: subtotal + iva };
}

/** Datos agrupados por carpeta para el pie-chart */
function calcPieData(rows = []) {
  const byFolder = rows.reduce(
    (acc, { listaNombre, cantidad = 0, precioUnitario = 0 }) => {
      const key = listaNombre?.trim() || "Sin carpeta";
      acc[key] = (acc[key] || 0) + cantidad * precioUnitario;
      return acc;
    },
    {}
  );
  return Object.entries(byFolder).map(([name, value]) => ({ name, value }));
}

/** Métricas globales del dashboard (ingresos, gastos, balance, total) */
function calcDashboard(rows = [], listas = []) {
  // Cada carpeta puede tener opcionalmente `tipo: "ingreso" | "gasto"`
  const tipoCarpeta = Object.fromEntries(
    listas.map((l) => [l.id, l.tipo || "gasto"])
  );

  let ingresos = 0;
  let gastos = 0;

  // Sumamos ingresos y gastos (sin IVA aún)
  rows.forEach(({ listaId, cantidad = 0, precioUnitario = 0 }) => {
    const monto = cantidad * precioUnitario;
    if (!monto) return;
    if (tipoCarpeta[listaId] === "ingreso") ingresos += monto;
    else gastos += monto;
  });

  const subtotal = ingresos + gastos;
  const iva = subtotal * 0.16;
  const totalConIva = subtotal + iva;

  return {
    totalCarpetas: totalConIva,
    ingresos,
    gastos,
    balance: ingresos - gastos,
  };
}

/* ------------------------------------------------------------------ */
/* ---------- HOOK ---------- */
export function useFunctionsPrevia() {
  return useMemo(
    () => ({
      formatDate,
      formatCurrency,
      trimLeadingZeros,
      calcTotals,
      calcPieData,
      calcDashboard,
    }),
    []
  );
}
